import React, { useState } from 'react';
import { ChevronDown, ChevronRight, RefreshCw } from 'lucide-react';
import EmptyState from './EmptyState';
import { isWhiteLabel } from '../../config/tenantConfig';
import { cn } from '../../utils/cn';
import type { PerBasketItem, PerBasketKpis } from '../../types/analytics';

export type BreakdownRowSelection =
  | { kind: 'basket'; basketId: string; basketName: string }
  | { kind: 'strategy'; basketId: string; strategyId: string; strategyName: string };

interface Props {
  items: PerBasketItem[];
  loading: boolean;
  error: string | null;
  selected: BreakdownRowSelection | null;
  onSelect: (sel: BreakdownRowSelection | null) => void;
}

const fmtInr = (n: number) =>
  `${n < 0 ? '-' : ''}₹${Math.abs(Math.round(n)).toLocaleString('en-IN')}`;
const fmtInrSigned = (n: number) => (n >= 0 ? '+' : '') + fmtInr(n);
const fmtNum = (n: number | null, decimals = 2) =>
  n === null || n === undefined ? '—' : n.toFixed(decimals);

const colorClass = (n: number | null | undefined) => {
  if (n === null || n === undefined) return 'text-gray-400 dark:text-gray-500';
  if (n > 0) return 'text-emerald-600 dark:text-emerald-400';
  if (n < 0) return 'text-red-600 dark:text-red-400';
  return 'text-gray-700 dark:text-gray-300';
};

/** Right-aligned cell. Greyed when the underlying KPI is null OR the row has
 *  zero trades (i.e. the basket/strategy has no activity in the filter range). */
const KpiCell: React.FC<{
  children: React.ReactNode;
  greyed?: boolean;
  className?: string;
}> = ({ children, greyed, className }) => (
  <td className={cn(
    'px-3 py-2 whitespace-nowrap text-right tabular-nums text-sm',
    greyed && 'text-gray-400 dark:text-gray-500',
    className,
  )}>
    {children}
  </td>
);

const renderKpiCells = (k: PerBasketKpis, greyed: boolean) => (
  <>
    <KpiCell greyed={greyed}>{k.totalTrades}</KpiCell>
    <KpiCell greyed={greyed}>{k.winRate.toFixed(1)}%</KpiCell>
    <KpiCell greyed={greyed} className={!greyed ? colorClass(k.netPnl) + ' font-semibold' : undefined}>
      {fmtInrSigned(k.netPnl)}
    </KpiCell>
    <KpiCell greyed={greyed}>{fmtNum(k.sharpeRatio)}</KpiCell>
    <KpiCell greyed={greyed}>{fmtNum(k.profitFactor)}</KpiCell>
    <KpiCell greyed={greyed} className={!greyed ? 'text-red-600 dark:text-red-400' : undefined}>
      -{fmtInr(k.maxDrawdown)}
    </KpiCell>
  </>
);

/**
 * Hierarchical Basket → Strategy KPI table for the Strategies section.
 *
 * Interaction model:
 *   - Chevron in the first column expands/collapses a basket's children;
 *     multiple baskets can be expanded simultaneously.
 *   - Clicking anywhere else on a row selects it for the drill-down panel
 *     rendered below. Click again to deselect.
 *   - Strategy rows are click-selectable; their drill-down scopes the
 *     analytics to that one strategy.
 */
const PerformanceBreakdownTable: React.FC<Props> = ({
  items, loading, error, selected, onSelect,
}) => {
  const wl = isWhiteLabel();
  const selectedRowBg = wl
    ? 'bg-zebu-50 dark:bg-zebu-900/30'
    : 'bg-blue-50 dark:bg-blue-900/30';

  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const toggle = (basketId: string) => {
    setExpanded((s) => {
      const next = new Set(s);
      if (next.has(basketId)) next.delete(basketId);
      else next.add(basketId);
      return next;
    });
  };

  const isBasketSelected = (basketId: string) =>
    selected?.kind === 'basket' && selected.basketId === basketId;
  const isStrategySelected = (basketId: string, strategyId: string) =>
    selected?.kind === 'strategy'
    && selected.basketId === basketId
    && selected.strategyId === strategyId;

  if (loading && items.length === 0) {
    return (
      <div className="py-12 flex items-center justify-center text-sm text-gray-500 dark:text-gray-400">
        <RefreshCw className="w-4 h-4 animate-spin mr-2" />
        Loading breakdown…
      </div>
    );
  }
  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 text-sm text-red-700 dark:text-red-300">
        {error}
      </div>
    );
  }
  if (items.length === 0) {
    return <EmptyState message="No baskets with activity in this range." />;
  }

  return (
    <>
      {/* Mobile (<md): card layout. Each basket is a stacked card with
          a chevron-toggle header, a 6-cell KPI grid, and nested strategy
          cards rendered inside when expanded. Tap anywhere outside the
          chevron to select the row for the drill-down. */}
      <div className="md:hidden divide-y divide-gray-200 dark:divide-gray-700">
        {items.map((b) => {
          const isOpen = expanded.has(b.basketId);
          const basketSelected = isBasketSelected(b.basketId);
          const basketGreyed = b.kpis.totalTrades === 0;
          return (
            <div key={b.basketId}>
              {/* Basket card */}
              <div
                className={cn(
                  'px-4 py-3 cursor-pointer',
                  basketSelected
                    ? selectedRowBg
                    : 'bg-white dark:bg-gray-800',
                )}
                onClick={() => onSelect(
                  basketSelected
                    ? null
                    : { kind: 'basket', basketId: b.basketId, basketName: b.basketName }
                )}
              >
                <div className="flex items-start gap-2">
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); toggle(b.basketId); }}
                    aria-label={isOpen ? 'Collapse' : 'Expand'}
                    disabled={b.strategies.length === 0}
                    className={cn(
                      'shrink-0 w-7 h-7 inline-flex items-center justify-center rounded mt-0.5',
                      b.strategies.length === 0
                        ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed'
                        : 'text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600',
                    )}
                  >
                    {isOpen
                      ? <ChevronDown className="w-4 h-4" />
                      : <ChevronRight className="w-4 h-4" />}
                  </button>
                  <div className="min-w-0 flex-1">
                    <div className={cn(
                      'font-semibold text-sm',
                      basketGreyed
                        ? 'text-gray-400 dark:text-gray-500'
                        : 'text-gray-900 dark:text-white',
                    )}>
                      {b.basketName}
                      {b.strategies.length > 0 && (
                        <span className="ml-1.5 text-xs font-normal text-gray-400 dark:text-gray-500">
                          ({b.strategies.length})
                        </span>
                      )}
                    </div>
                    <KpiGrid kpis={b.kpis} greyed={basketGreyed} />
                  </div>
                </div>
              </div>

              {/* Nested strategy cards */}
              {isOpen && b.strategies.map((s) => {
                const strategySelected = isStrategySelected(b.basketId, s.strategyId);
                const strategyGreyed = s.kpis.totalTrades === 0;
                return (
                  <div
                    key={s.strategyId}
                    className={cn(
                      'px-4 py-3 pl-12 cursor-pointer border-t border-gray-100 dark:border-gray-700/60',
                      strategySelected
                        ? selectedRowBg
                        : 'bg-gray-50/50 dark:bg-gray-800/60',
                    )}
                    onClick={() => onSelect(
                      strategySelected
                        ? null
                        : {
                            kind: 'strategy',
                            basketId: b.basketId,
                            strategyId: s.strategyId,
                            strategyName: s.strategyName,
                          }
                    )}
                  >
                    <div className={cn(
                      'text-sm',
                      strategyGreyed
                        ? 'text-gray-400 dark:text-gray-500'
                        : 'text-gray-800 dark:text-gray-200',
                    )}>
                      <span className="text-gray-400 dark:text-gray-500 mr-1.5">↳</span>
                      {s.strategyName}
                    </div>
                    <KpiGrid kpis={s.kpis} greyed={strategyGreyed} />
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>

      {/* Desktop (md+): table */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 dark:bg-gray-700 border-y border-gray-200 dark:border-gray-700">
              <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">
                Name
              </th>
              <th className="px-3 py-2 text-right text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">Trades</th>
              <th className="px-3 py-2 text-right text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">Win %</th>
              <th className="px-3 py-2 text-right text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">Net P&amp;L</th>
              <th className="px-3 py-2 text-right text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">Sharpe</th>
              <th className="px-3 py-2 text-right text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">PF</th>
              <th className="px-3 py-2 text-right text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">Max DD</th>
            </tr>
          </thead>
          <tbody>
            {items.map((b) => {
              const isOpen = expanded.has(b.basketId);
              const basketSelected = isBasketSelected(b.basketId);
              const basketGreyed = b.kpis.totalTrades === 0;
              return (
                <React.Fragment key={b.basketId}>
                  <tr
                    className={cn(
                      'border-b border-gray-100 dark:border-gray-700 cursor-pointer',
                      basketSelected
                        ? selectedRowBg
                        : 'bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700',
                    )}
                    onClick={() => onSelect(
                      basketSelected
                        ? null
                        : { kind: 'basket', basketId: b.basketId, basketName: b.basketName }
                    )}
                  >
                    <td className="px-3 py-2 whitespace-nowrap">
                      <div className="flex items-center gap-1.5">
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); toggle(b.basketId); }}
                          aria-label={isOpen ? 'Collapse' : 'Expand'}
                          disabled={b.strategies.length === 0}
                          className={cn(
                            'shrink-0 w-5 h-5 inline-flex items-center justify-center rounded',
                            b.strategies.length === 0
                              ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed'
                              : 'text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600',
                          )}
                        >
                          {isOpen
                            ? <ChevronDown className="w-3.5 h-3.5" />
                            : <ChevronRight className="w-3.5 h-3.5" />}
                        </button>
                        <span className={cn(
                          'font-semibold',
                          basketGreyed
                            ? 'text-gray-400 dark:text-gray-500'
                            : 'text-gray-900 dark:text-white',
                        )}>
                          {b.basketName}
                        </span>
                        {b.strategies.length > 0 && (
                          <span className="text-xs text-gray-400 dark:text-gray-500">
                            ({b.strategies.length})
                          </span>
                        )}
                      </div>
                    </td>
                    {renderKpiCells(b.kpis, basketGreyed)}
                  </tr>

                  {isOpen && b.strategies.map((s) => {
                    const strategySelected = isStrategySelected(b.basketId, s.strategyId);
                    const strategyGreyed = s.kpis.totalTrades === 0;
                    return (
                      <tr
                        key={s.strategyId}
                        className={cn(
                          'border-b border-gray-100 dark:border-gray-700/60 cursor-pointer',
                          strategySelected
                            ? selectedRowBg
                            : 'bg-gray-50/50 dark:bg-gray-800/60 hover:bg-gray-100 dark:hover:bg-gray-700',
                        )}
                        onClick={() => onSelect(
                          strategySelected
                            ? null
                            : {
                                kind: 'strategy',
                                basketId: b.basketId,
                                strategyId: s.strategyId,
                                strategyName: s.strategyName,
                              }
                        )}
                      >
                        <td className="px-3 py-2 whitespace-nowrap">
                          <div className="flex items-center gap-1.5 pl-7">
                            <span className="text-gray-400 dark:text-gray-500">↳</span>
                            <span className={cn(
                              strategyGreyed
                                ? 'text-gray-400 dark:text-gray-500'
                                : 'text-gray-800 dark:text-gray-200',
                            )}>
                              {s.strategyName}
                            </span>
                          </div>
                        </td>
                        {renderKpiCells(s.kpis, strategyGreyed)}
                      </tr>
                    );
                  })}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
};

/** Mobile KPI grid — same 6 metrics as the desktop table row, laid out as
 *  a 3-col x 2-row grid so each value gets a labelled cell without forcing
 *  horizontal scroll. */
const KpiGrid: React.FC<{ kpis: PerBasketKpis; greyed: boolean }> = ({ kpis, greyed }) => {
  const items: Array<{ label: string; value: React.ReactNode; cls?: string }> = [
    { label: 'Trades', value: kpis.totalTrades },
    { label: 'Win %', value: `${kpis.winRate.toFixed(1)}%` },
    {
      label: 'Net P&L',
      value: fmtInrSigned(kpis.netPnl),
      cls: !greyed ? colorClass(kpis.netPnl) + ' font-semibold' : undefined,
    },
    { label: 'Sharpe', value: fmtNum(kpis.sharpeRatio) },
    { label: 'PF', value: fmtNum(kpis.profitFactor) },
    {
      label: 'Max DD',
      value: `-${fmtInr(kpis.maxDrawdown)}`,
      cls: !greyed ? 'text-red-600 dark:text-red-400' : undefined,
    },
  ];
  return (
    <dl className="mt-2 grid grid-cols-3 gap-x-3 gap-y-2 text-xs">
      {items.map((i) => (
        <div key={i.label}>
          <dt className="text-gray-500 dark:text-gray-400 uppercase tracking-wider">{i.label}</dt>
          <dd className={cn(
            'mt-0.5 text-sm tabular-nums',
            greyed ? 'text-gray-400 dark:text-gray-500' : 'text-gray-900 dark:text-white',
            i.cls,
          )}>
            {i.value}
          </dd>
        </div>
      ))}
    </dl>
  );
};

export default PerformanceBreakdownTable;
